import React, { useEffect, useState } from "react";
import Star from "@mui/icons-material/Star";
import Visibility from "@mui/icons-material/Visibility";
import Verified from "@mui/icons-material/Verified";
import ArrowForward from "@mui/icons-material/ArrowForward";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Warning from "@mui/icons-material/Warning";
import TrendingUp from "@mui/icons-material/TrendingUp";
import Info from "@mui/icons-material/Info";
import { focusAreas } from "./features";
import { ReputationResult, ReputationScores } from "./questions";

const ReputationResults: React.FC = () => {
  const [animationStep, setAnimationStep] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);

    const timer = setInterval(() => {
      setAnimationStep((prev) => (prev < 3 ? prev + 1 : prev));
    }, 300);

    return () => {
      clearInterval(timer);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const savedResults = localStorage.getItem("reputation_results");
  const results: ReputationResult[] = savedResults
    ? JSON.parse(savedResults)
    : [];

  const calculateScores = (): ReputationScores => {
    return results.reduce(
      (acc: ReputationScores, result: ReputationResult) => ({
        reviewManagement: acc.reviewManagement + result.scores.reviewManagement,
        onlineMonitoring: acc.onlineMonitoring + result.scores.onlineMonitoring,
        brandTrust: acc.brandTrust + result.scores.brandTrust,
      }),
      { reviewManagement: 0, onlineMonitoring: 0, brandTrust: 0 }
    );
  };

  const scores = calculateScores();
  const maxScore = results.length * 3;

  let primaryFocus: keyof typeof focusAreas = "reviewManagement";
  if (
    scores.onlineMonitoring > scores.reviewManagement &&
    scores.onlineMonitoring >= scores.brandTrust
  ) {
    primaryFocus = "onlineMonitoring";
  } else if (
    scores.brandTrust > scores.reviewManagement &&
    scores.brandTrust > scores.onlineMonitoring
  ) {
    primaryFocus = "brandTrust";
  }

  const getScorePercentage = (score: number) =>
    Math.round((score / maxScore) * 100);

  const handleRestart = () => {
    localStorage.removeItem("reputation_results");
    window.location.href = "/reputation";
  };

  const getIcon = (area: keyof typeof focusAreas, isLarge = false) => {
    const size = isLarge ? 48 : 40;
    const iconProps = { sx: { fontSize: size, color: "#c9a750" } };

    switch (area) {
      case "reviewManagement":
        return <Star {...iconProps} />;
      case "onlineMonitoring":
        return <Visibility {...iconProps} />;
      case "brandTrust":
        return <Verified {...iconProps} />;
      default:
        return null;
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #152A53 100%)",
        padding: isMobile ? "20px" : "40px 20px",
      }}
    >
      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
        }}
      >
        {/* Header Section */}
        <div
          style={{
            textAlign: "center",
            marginBottom: "50px",
            opacity: animationStep >= 1 ? 1 : 0,
            transform:
              animationStep >= 1 ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.6s ease",
          }}
        >
          <h1
            style={{
              fontSize: isMobile ? "32px" : "48px",
              fontWeight: "bold",
              color: "white",
              marginBottom: "15px",
            }}
          >
            Your Reputation Management Assessment
          </h1>
          <p
            style={{
              fontSize: isMobile ? "16px" : "20px",
              color: "rgba(255, 255, 255, 0.9)",
              maxWidth: "800px",
              margin: "0 auto",
            }}
          >
            Based on your responses, here's your personalized roadmap for
            building trust with potential customers
          </p>
        </div>

        {/* Score Overview Cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
            gap: "20px",
            marginBottom: "40px",
            opacity: animationStep >= 2 ? 1 : 0,
            transform:
              animationStep >= 2 ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.6s ease 0.2s",
          }}
        >
          {(Object.keys(focusAreas) as Array<keyof typeof focusAreas>).map(
            (area) => (
              <div
                key={area}
                style={{
                  backgroundColor: "white",
                  borderRadius: "15px",
                  padding: "25px",
                  boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)",
                  textAlign: "center",
                  position: "relative",
                  border: area === primaryFocus ? "3px solid #c9a750" : "none",
                  transform: "translateY(0)",
                  transition: "transform 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-5px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                {area === primaryFocus && (
                  <div
                    style={{
                      position: "absolute",
                      top: "-12px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      backgroundColor: "#c9a750",
                      color: "white",
                      padding: "4px 16px",
                      borderRadius: "12px",
                      fontSize: "12px",
                      fontWeight: "bold",
                    }}
                  >
                    Start Here
                  </div>
                )}
                <div style={{ marginBottom: "15px" }}>{getIcon(area)}</div>
                <h3
                  style={{
                    fontSize: "18px",
                    fontWeight: "bold",
                    color: "#1a202c",
                    marginBottom: "10px",
                  }}
                >
                  {focusAreas[area].title}
                </h3>
                <div
                  style={{
                    fontSize: "32px",
                    fontWeight: "bold",
                    color: "#c9a750",
                  }}
                >
                  {getScorePercentage(scores[area])}%
                </div>
              </div>
            )
          )}
        </div>

        {/* Primary Focus Details Card */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "20px",
            padding: isMobile ? "30px" : "50px",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
            marginBottom: "30px",
            opacity: animationStep >= 3 ? 1 : 0,
            transform:
              animationStep >= 3 ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.6s ease 0.4s",
          }}
        >
          {/* Card Header */}
          <div
            style={{
              textAlign: "center",
              marginBottom: "40px",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "80px",
                height: "80px",
                borderRadius: "50%",
                backgroundColor: "#fff7e6",
                marginBottom: "20px",
              }}
            >
              {getIcon(primaryFocus, true)}
            </div>
            <h2
              style={{
                fontSize: isMobile ? "24px" : "32px",
                fontWeight: "bold",
                color: "#1a202c",
                marginBottom: "10px",
              }}
            >
              {focusAreas[primaryFocus].title}
            </h2>
            <p
              style={{
                fontSize: "18px",
                color: "#4a5568",
              }}
            >
              Your highest priority area for immediate improvement
            </p>
          </div>

          {/* Two Column Layout for Challenges and Business Impact */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: "30px",
              marginBottom: "40px",
            }}
          >
            {/* Current Challenges */}
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  marginBottom: "15px",
                }}
              >
                <Warning sx={{ color: "#f59e0b", fontSize: 28 }} />
                <h3
                  style={{
                    fontSize: "20px",
                    fontWeight: "bold",
                    color: "#1a202c",
                  }}
                >
                  Current Challenges
                </h3>
              </div>
              <ul
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: 0,
                }}
              >
                {focusAreas[primaryFocus].symptoms.map((symptom, index) => (
                  <li
                    key={index}
                    style={{
                      padding: "12px 0",
                      borderBottom:
                        index < focusAreas[primaryFocus].symptoms.length - 1
                          ? "1px solid #e2e8f0"
                          : "none",
                      color: "#4a5568",
                      lineHeight: "1.6",
                    }}
                  >
                    • {symptom}
                  </li>
                ))}
              </ul>
            </div>

            {/* Business Impact */}
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  marginBottom: "15px",
                }}
              >
                <TrendingUp sx={{ color: "#ef4444", fontSize: 28 }} />
                <h3
                  style={{
                    fontSize: "20px",
                    fontWeight: "bold",
                    color: "#1a202c",
                  }}
                >
                  Impact on Your Business
                </h3>
              </div>
              <ul
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: 0,
                }}
              >
                {focusAreas[primaryFocus].impacts.map((impact, index) => (
                  <li
                    key={index}
                    style={{
                      padding: "12px 0",
                      borderBottom:
                        index < focusAreas[primaryFocus].impacts.length - 1
                          ? "1px solid #e2e8f0"
                          : "none",
                      color: "#4a5568",
                      lineHeight: "1.6",
                    }}
                  >
                    • {impact}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Solutions Section */}
          <div style={{ marginBottom: "40px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                marginBottom: "15px",
              }}
            >
              <CheckCircle sx={{ color: "#10b981", fontSize: 28 }} />
              <h3
                style={{
                  fontSize: "20px",
                  fontWeight: "bold",
                  color: "#1a202c",
                }}
              >
                Our Solutions
              </h3>
            </div>
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
              }}
            >
              {focusAreas[primaryFocus].solutions.map((solution, index) => (
                <li
                  key={index}
                  style={{
                    padding: "12px 0",
                    borderBottom:
                      index < focusAreas[primaryFocus].solutions.length - 1
                        ? "1px solid #e2e8f0"
                        : "none",
                    color: "#4a5568",
                    lineHeight: "1.6",
                  }}
                >
                  • {solution}
                </li>
              ))}
            </ul>
          </div>

          {/* Next Steps */}
          <div style={{ marginBottom: "30px" }}>
            <h3
              style={{
                fontSize: "20px",
                fontWeight: "bold",
                color: "#1a202c",
                marginBottom: "20px",
              }}
            >
              Immediate Next Steps
            </h3>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "15px" }}
            >
              {focusAreas[primaryFocus].nextSteps.map((step, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "15px",
                  }}
                >
                  <div
                    style={{
                      width: "30px",
                      height: "30px",
                      borderRadius: "50%",
                      backgroundColor: "#c9a750",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "bold",
                      flexShrink: 0,
                    }}
                  >
                    {index + 1}
                  </div>
                  <p
                    style={{
                      color: "#4a5568",
                      lineHeight: "1.6",
                      margin: 0,
                      paddingTop: "4px",
                    }}
                  >
                    {step}
                  </p>
                  <ArrowForward
                    sx={{
                      color: "#c9a750",
                      fontSize: 20,
                      marginTop: "5px",
                      flexShrink: 0,
                    }}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Key Insight */}
          <div
            style={{
              backgroundColor: "#fff7e6",
              borderLeft: "4px solid #c9a750",
              borderRadius: "8px",
              padding: "20px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "15px",
              }}
            >
              <Info sx={{ color: "#c9a750", fontSize: 24, flexShrink: 0 }} />
              <p
                style={{
                  color: "#1a202c",
                  lineHeight: "1.6",
                  margin: 0,
                  fontWeight: "500",
                }}
              >
                {focusAreas[primaryFocus].insight}
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "20px",
            padding: isMobile ? "30px" : "50px",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
            textAlign: "center",
            opacity: animationStep >= 3 ? 1 : 0,
            transform:
              animationStep >= 3 ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.6s ease 0.6s",
          }}
        >
          <h2
            style={{
              fontSize: isMobile ? "24px" : "32px",
              fontWeight: "bold",
              color: "#1a202c",
              marginBottom: "15px",
            }}
          >
            Ready to Build an Unshakeable Reputation?
          </h2>
          <p
            style={{
              fontSize: "18px",
              color: "#4a5568",
              marginBottom: "30px",
              maxWidth: "700px",
              margin: "0 auto 30px",
            }}
          >
            Let's create a reputation management strategy that builds trust and
            drives more Saskatchewan customers to choose your business.
          </p>
          <div
            style={{
              display: "flex",
              gap: "15px",
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <a
              href="https://go.intheraconsultinggroup.com/widget/booking/TJJGwaGSkks85xN6ejvL"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-block",
                padding: "15px 40px",
                backgroundColor: "#c9a750",
                color: "white",
                textDecoration: "none",
                borderRadius: "8px",
                fontSize: "18px",
                fontWeight: "600",
                transition: "all 0.3s ease",
                border: "none",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#b8963d";
                e.currentTarget.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#c9a750";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              Book Digital Strategy Session
            </a>
            <button
              onClick={handleRestart}
              style={{
                padding: "15px 40px",
                backgroundColor: "white",
                color: "#4a5568",
                border: "2px solid #e2e8f0",
                borderRadius: "8px",
                fontSize: "18px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f7fafc";
                e.currentTarget.style.borderColor = "#cbd5e0";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "white";
                e.currentTarget.style.borderColor = "#e2e8f0";
              }}
            >
              Retake Assessment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReputationResults;
